import api from './api';

/**
 * Search Service - handles API calls for entity searches
 */
class SearchService {
  /**
   * Search all entities based on query and filter
   */
  async searchAll(query, filter = 'all') {
    const results = {
      members: [],
      bookings: [],
      volunteers: [],
      calls: []
    };

    if (!query || query.length < 2) {
      return results;
    }

    try {
      // Build search promises based on filter
      const promises = [];

      if (filter === 'all' || filter === 'members') {
        promises.push(
          this.searchMembers(query).then(data => { results.members = data; })
        );
      }

      if (filter === 'all' || filter === 'bookings') {
        promises.push(
          this.searchBookings(query).then(data => { results.bookings = data; })
        );
      }

      if (filter === 'all' || filter === 'volunteers') {
        promises.push(
          this.searchVolunteers(query).then(data => { results.volunteers = data; })
        );
      }

      if (filter === 'all' || filter === 'calls') {
        promises.push(
          this.searchCalls(query).then(data => { results.calls = data; })
        );
      }

      await Promise.all(promises);
    } catch (error) {
      console.error('Search all error:', error);
    }

    return results;
  }

  /**
   * Search members/patients
   */
  async searchMembers(query) {
    try {
      const response = await api.get('/screening/search-patients', { query, limit: 5 });
      if (response.success && response.data) {
        return response.data.map(member => ({
          _id: member._id,
          key: member._id,
          label: `${member.firstName || ''} ${member.lastName || ''}`.trim() || 'Unknown',
          subtitle: member.email || member.phone || '',
          data: member
        }));
      }
      return [];
    } catch (error) {
      console.error('Search members error:', error);
      return [];
    }
  }

  /**
   * Search bookings (public event appointments)
   */
  async searchBookings(query) {
    try {
      const response = await api.get('/admin/bookings', { search: query, limit: 5 });
      if (response.success && response.data) {
        // Handle nested structure: response.data.bookings or response.data
        const bookings = response.data.bookings || response.data.data || response.data;
        if (!Array.isArray(bookings)) return [];
        
        return bookings.slice(0, 5).map(booking => {
          // User info is nested under userInfo
          const firstName = booking.userInfo?.firstName || booking.firstName || '';
          const lastName = booking.userInfo?.lastName || booking.lastName || '';
          const fullName = `${firstName} ${lastName}`.trim();
          const eventName = booking.eventType?.name || booking.eventTypeName || 'Event';
          const eventDate = booking.timeSlot?.date || booking.bookingDate || booking.eventDate;
          
          return {
            _id: booking._id,
            key: booking._id,
            label: fullName || booking.confirmationCode || 'Unknown Booking',
            subtitle: eventDate 
              ? `${new Date(eventDate).toLocaleDateString()} - ${eventName}`
              : booking.confirmationCode || '',
            data: booking
          };
        });
      }
      return [];
    } catch (error) {
      console.error('Search bookings error:', error);
      return [];
    }
  }

  /**
   * Search volunteers
   */
  async searchVolunteers(query) {
    try {
      const response = await api.get('/volunteer-registrations', { search: query, limit: 5 });
      if (response.success && response.data) {
        return response.data.map(vol => ({
          _id: vol._id,
          key: vol._id,
          label: vol.fullName || vol.email || 'Unknown Volunteer',
          subtitle: vol.preferredCity ? `${vol.preferredCity}, ${vol.preferredState || ''}` : '',
          data: vol
        }));
      }
      return [];
    } catch (error) {
      console.error('Search volunteers error:', error);
      return [];
    }
  }

  /**
   * Search call logs
   */
  async searchCalls(query) {
    try {
      const response = await api.get('/voice-calls', { search: query, limit: 5 });
      if (response.success && response.data) {
        return response.data.map(call => ({
          _id: call._id,
          key: call._id,
          label: call.patientName || call.patientPhone || 'Unknown Call',
          subtitle: call.initiatedAt ? new Date(call.initiatedAt).toLocaleDateString() : '',
          data: call
        }));
      }
      return [];
    } catch (error) {
      console.error('Search calls error:', error);
      return [];
    }
  }
}

export default new SearchService();
